import React, { useRef, useState, useMemo, useEffect, Suspense } from "react"
import { Canvas, useFrame } from "@react-three/fiber"
import { OrbitControls, Environment, useGLTF, useTexture } from "@react-three/drei"
import * as THREE from "three"
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material"

// ── Surfboard Model ─────────────────────────────────────────────────────

const MODEL_MAP = {
  'Round': {
    'Diamond': '/static/models/RoundNoseDiamondTail.glb',
    'Square': '/static/models/RoundNoseSquareTail.glb?v=4',
    'Round': '/static/models/RoundNoseRoundTail.glb',
    'Pin': '/static/models/RoundNosePinTail.glb',
    'Crescent': '/static/models/RoundNoseSquareTail.glb?v=4',
    '_default': '/static/models/RoundNoseSquareTail.glb?v=4',
  },
  'Performance': {
    'Diamond': '/static/models/PerformanceNoseDiamondTail.glb?v=2',
    'Square': '/static/models/PerformanceNoseSquareTail.glb?v=2',
    'Squash': '/static/models/PerformanceNoseSquashTail.glb?v=2',
    'Round': '/static/models/PerformanceNoseRoundTail.glb?v=2',
    '_default': '/static/models/PerformanceNoseSquashTail.glb?v=2',
  },
  'Hybrid': {
    'Diamond': '/static/models/HybridNoseDiamondTail.glb',
    'Square': '/static/models/HybridNoseSquareTail.glb',
    'Squash': '/static/models/HybridNoseSquashTail.glb',
    'Round': '/static/models/HybridNoseRoundTail.glb',
    'Pin': '/static/models/HybridNosePinTail.glb',
    '_default': '/static/models/HybridNoseSquashTail.glb',
  },
  '_default': '/static/models/RoundNoseSquareTail.glb?v=4',
}

function getModelUrl(noseShape, tailShape) {
  const noseMap = MODEL_MAP[noseShape] || MODEL_MAP['_default']
  if (typeof noseMap === 'string') return noseMap
  return noseMap[tailShape] || noseMap['_default'] || MODEL_MAP['_default']
}

// ── Patch shape functions ─────────────────────────────────────────────
// Return true if a vertex at normalized position (t=0 nose, t=1 tail)
// and width fraction (wFrac = |w| / maxSliceWidth, 0=center, 1=rail)
// is inside the patch. Derived from the SVG patch paths.

// ── Patch shape functions ─────────────────────────────────────────────
// t: 0=nose, 1=tail. wFrac: 0=center, 1=rail.
// Returns 0-1 intensity (smooth falloff at edges) instead of boolean.

function smoothstep(edge0, edge1, x) {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)))
  return t * t * (3 - 2 * t)
}

const E = 0.03 // edge softness for anti-aliased patch boundaries

const DECK_PATCH_FNS = {
  "None": () => 0,
  "Straight Point": (t, isLB, wFrac) => {
    const start = isLB ? 0.22 : 0.35
    const end = isLB ? 0.78 : 1.0
    const tipLen = 0.08
    if (t < start - E || t > end + E) return 0
    if (t < start + tipLen) {
      const wl = Math.max(0, (t - start) / tipLen)
      return smoothstep(wl + E, wl - E, wFrac)
    }
    if (isLB && t > end - tipLen) {
      const wl = Math.max(0, (end - t) / tipLen)
      return smoothstep(wl + E, wl - E, wFrac)
    }
    return 1
  },
  "Curved Point": (t, isLB, wFrac) => {
    const start = isLB ? 0.20 : 0.32
    const end = isLB ? 0.78 : 1.0
    const tipLen = 0.12
    if (t < start - E || t > end + E) return 0
    if (t < start + tipLen) {
      const f = Math.max(0, (t - start) / tipLen)
      const wl = f * f
      return smoothstep(wl + E, wl - E, wFrac)
    }
    if (isLB && t > end - tipLen) {
      const f = Math.max(0, (end - t) / tipLen)
      const wl = f * f
      return smoothstep(wl + E, wl - E, wFrac)
    }
    return 1
  },
  "Round": (t, isLB, wFrac) => {
    const start = isLB ? 0.22 : 0.35
    const end = isLB ? 0.78 : 1.0
    const tipLen = 0.10
    if (t < start - E || t > end + E) return 0
    if (t < start + tipLen) {
      const f = Math.max(0, (t - start) / tipLen)
      const wl = Math.sqrt(f)
      return smoothstep(wl + E, wl - E, wFrac)
    }
    if (isLB && t > end - tipLen) {
      const f = Math.max(0, (end - t) / tipLen)
      const wl = Math.sqrt(f)
      return smoothstep(wl + E, wl - E, wFrac)
    }
    return 1
  },
}

const TAIL_PATCH_FNS = {
  "None": () => 0,
  "Straight Point": (t, _isLB, wFrac) => {
    const start = 0.74
    const tipLen = 0.09
    if (t < start - E) return 0
    if (t < start + tipLen) {
      const wl = Math.max(0, (t - start) / tipLen)
      return smoothstep(wl + E, wl - E, wFrac)
    }
    return 1
  },
  "Reverse Straight Point": (t, _isLB, wFrac) => {
    const start = 0.69
    const tipLen = 0.09
    if (t < start - E) return 0
    if (t < start + tipLen) {
      const wl = Math.max(0, (t - start) / tipLen)
      return smoothstep(1 - wl - E, 1 - wl + E, wFrac)
    }
    return 1
  },
  "Curved Point": (t, _isLB, wFrac) => {
    const start = 0.68
    const tipLen = 0.11
    if (t < start - E) return 0
    if (t < start + tipLen) {
      const f = Math.max(0, (t - start) / tipLen)
      const wl = f * f
      return smoothstep(wl + E, wl - E, wFrac)
    }
    return 1
  },
  "Reverse Curved Point": (t, _isLB, wFrac) => {
    const start = 0.67
    const tipLen = 0.11
    if (t < start - E) return 0
    if (t < start + tipLen) {
      const f = Math.max(0, (t - start) / tipLen)
      const wl = f * f
      return smoothstep(1 - wl - E, 1 - wl + E, wFrac)
    }
    return 1
  },
  "Round": (t, _isLB, wFrac) => {
    const start = 0.75
    const tipLen = 0.09
    if (t < start - E) return 0
    if (t < start + tipLen) {
      const f = Math.max(0, (t - start) / tipLen)
      const wl = Math.sqrt(f)
      return smoothstep(wl + E, wl - E, wFrac)
    }
    return 1
  },
}

function SurfboardModel({ modelUrl, deckColor, bottomColor, stringerColor, isGloss, logoType, deckPatch, tailPatch, finColor, finSetup, stringerCount, onRotation, lengthInches, widthInches, thicknessInches, pinLineColor }) {
  const { scene } = useGLTF(modelUrl)
  const meshRef = useRef()
  const diamondTex = useTexture("/static/models/marlin-logo.png")
  const fishTex = useTexture("/static/models/marlin-fish-logo.png")
  diamondTex.colorSpace = THREE.SRGBColorSpace
  fishTex.colorSpace = THREE.SRGBColorSpace

  const logoTex = logoType === 'fish' ? fishTex : diamondTex

  const data = useMemo(() => {
    const clone = scene.clone()
    const bbox = new THREE.Box3().setFromObject(clone)
    const center = bbox.getCenter(new THREE.Vector3())
    const size = bbox.getSize(new THREE.Vector3())
    clone.position.sub(center)

    const dims = [
      { idx: 0, val: size.x, axis: 'x' },
      { idx: 1, val: size.y, axis: 'y' },
      { idx: 2, val: size.z, axis: 'z' },
    ].sort((a, b) => b.val - a.val)
    const lengthIdx = dims[0].idx
    const widthIdx = dims[1].idx
    const thickIdx = dims[2].idx
    const boardLength = dims[0].val
    const boardWidth = dims[1].val
    const boardThick = dims[2].val
    // Performance models have deck/bottom flipped vs Round nose
    const isPerf = modelUrl.includes('Performance')
    const deckSign = isPerf ? 1 : -1  // deck normal direction

    const deckRaw = new THREE.Color(deckColor)
    const bottomRaw = new THREE.Color(bottomColor)
    const deckIsClear = deckRaw.getHex() === 0xf5f5f5 || deckRaw.getHex() === 0xffffff
    const bottomIsClear = bottomRaw.getHex() === 0xf5f5f5 || bottomRaw.getHex() === 0xffffff
    // Lighten colors for transparent resin tint look
    const white = new THREE.Color(0xffffff)
    const deck = deckRaw
    const bottom = bottomRaw

    // Collect stringer dups to add AFTER traverse (adding during traverse corrupts it)
    const pendingDups = []

    clone.traverse((child) => {
      if (!child.isMesh) return
      const nameLC = (child.name || '').toLowerCase()
      const isStringer = nameLC === 'cube' || nameLC.includes('stringer')
      const isFinBox = !isStringer && !nameLC.includes('surfboard') && (nameLC.includes('fin') || nameLC.includes('cube') || nameLC.includes('box'))

      if (isFinBox) {
        const hasCenterBox = finSetup === "Single" || finSetup === "2+1" || finSetup === "4+1"
        child.visible = hasCenterBox
        if (hasCenterBox) {
          child.material = new THREE.MeshPhysicalMaterial({
            color: new THREE.Color(finColor),
            clearcoat: isGloss ? 0.4 : 0.0,
            roughness: 0.3,
            metalness: 0.0,
            side: THREE.DoubleSide,
          })
        }
        return
      }

      if (isStringer) {
        child.visible = false // all stringer rendering done via vertex colors
        child.material = new THREE.MeshPhysicalMaterial({
          color: new THREE.Color(stringerColor),
          clearcoat: isGloss ? 0.6 : 0.0,
          clearcoatRoughness: isGloss ? 0.1 : 0,
          roughness: 0.45,
          metalness: 0.0,
          side: THREE.DoubleSide,
        })
        return
      }


      // Deep-clone geometry so we don't corrupt the shared original
      const geo = child.geometry.clone()
      child.geometry = geo
      if (geo.attributes.color) geo.deleteAttribute('color')
      if (!geo.attributes.normal || !geo.attributes.position) return
      const normals = geo.attributes.normal.array
      const positions = geo.attributes.position.array
      const count = normals.length / 3
      const colors = new Float32Array(normals.length)

      child.updateWorldMatrix(true, false)
      const normalMatrix = new THREE.Matrix3().getNormalMatrix(child.matrixWorld)

      const SLICES = 800
      const worldPos = new THREE.Vector3()
      const worldNorm = new THREE.Vector3()
      let minL = Infinity, maxL = -Infinity
      const worldPositions = new Float32Array(count * 3)
      const worldNormals = new Float32Array(count * 3)

      for (let i = 0; i < count; i++) {
        worldPos.set(positions[i*3], positions[i*3+1], positions[i*3+2])
        worldPos.applyMatrix4(child.matrixWorld)
        worldNorm.set(normals[i*3], normals[i*3+1], normals[i*3+2])
        worldNorm.applyMatrix3(normalMatrix).normalize()

        worldPositions[i*3] = worldPos.x - center.x
        worldPositions[i*3+1] = worldPos.y - center.y
        worldPositions[i*3+2] = worldPos.z - center.z
        worldNormals[i*3] = worldNorm.x
        worldNormals[i*3+1] = worldNorm.y
        worldNormals[i*3+2] = worldNorm.z

        const l = worldPositions[i*3 + lengthIdx]
        if (l < minL) minL = l
        if (l > maxL) maxL = l
      }

      const lRange = maxL - minL || 1
      const sliceMaxW = new Float32Array(SLICES).fill(0)

      for (let i = 0; i < count; i++) {
        const l = worldPositions[i*3 + lengthIdx]
        const w = Math.abs(worldPositions[i*3 + widthIdx])
        const sliceIdx = Math.min(Math.floor(((l - minL) / lRange) * SLICES), SLICES - 1)
        if (w > sliceMaxW[sliceIdx]) sliceMaxW[sliceIdx] = w
      }

      for (let i = 0; i < SLICES; i++) {
        if (sliceMaxW[i] === 0) {
          let prev = i - 1, next = i + 1
          while (prev >= 0 && sliceMaxW[prev] === 0) prev--
          while (next < SLICES && sliceMaxW[next] === 0) next++
          const pv = prev >= 0 ? sliceMaxW[prev] : 0
          const nv = next < SLICES ? sliceMaxW[next] : 0
          if (prev >= 0 && next < SLICES) {
            sliceMaxW[i] = pv + (nv - pv) * ((i - prev) / (next - prev))
          } else {
            sliceMaxW[i] = pv || nv
          }
        }
      }

      const globalMaxW = Math.max(...sliceMaxW)
      const overlapDist = globalMaxW * 0.20
      const deckPatchFn = DECK_PATCH_FNS[deckPatch] || DECK_PATCH_FNS["None"]
      const tailPatchFn = TAIL_PATCH_FNS[tailPatch] || TAIL_PATCH_FNS["None"]
      const isLongboard = modelUrl.includes('RoundNose')

      for (let i = 0; i < count; i++) {
        const nThick = worldNormals[i*3 + thickIdx]
        const thickPos = worldPositions[i*3 + thickIdx]
        const w = Math.abs(worldPositions[i*3 + widthIdx])
        const lPos = worldPositions[i*3 + lengthIdx]
        const sliceIdx = Math.min(Math.floor(((lPos - minL) / lRange) * SLICES), SLICES - 1)
        const maxW = sliceMaxW[sliceIdx] || 1
        const sliceOverlap = maxW * 0.20 // per-slice overlap, shrinks at nose/tail
        const inSideOverlap = (maxW - w) < sliceOverlap
        const inOverlap = inSideOverlap

        // t: 0 = nose, 1 = tail (negative length = tail in model space)
        const t = 1 - ((lPos - minL) / lRange)

        // Use normal for clear deck/bottom, fall back to position when ambiguous
        const isDeckFace = Math.abs(nThick) > 0.15 ? (nThick * deckSign > 0) : (thickPos * deckSign > 0)

        // Smooth the overlap boundary by blending based on distance into overlap
        const overlapFrac = inOverlap ? Math.min(1, (sliceOverlap - (maxW - w)) / (sliceOverlap * 0.9)) : 0

        let c
        if (inOverlap) {
          // Same color on both faces to prevent z-fighting flicker
          if (deckIsClear && bottomIsClear) {
            c = deck.clone()
          } else if (deckIsClear) {
            c = bottom.clone()
          } else if (bottomIsClear) {
            c = deck.clone()
          } else {
            // Both colored — blend toward the wrapping color
            c = bottom.clone()
            c.lerp(deck, 0.5)
            c.multiplyScalar(0.5 + (1 - overlapFrac) * 0.2)
          }
        } else if (Math.abs(nThick) < 0.15) {
          // Rail — use blended color
          if (deckIsClear || bottomIsClear) {
            c = (deckIsClear ? bottom : deck).clone()
          } else {
            c = deck.clone().lerp(bottom, 0.5)
            c.multiplyScalar(0.55)
          }
        } else if (isDeckFace) {
          c = deck.clone()
        } else {
          c = bottom.clone()
        }

        // Apply patch tinting — deck patch on deck only, tail patch on bottom only
        // Sample nearby t/wFrac offsets and average for smoother edges
        {
          const wFrac = maxW > 0 ? w / maxW : 0
          const dt = 0.005, dw = 0.012
          // Check both deck and tail patch — apply whichever is active
          let dSum = 0, tSum = 0
          for (let ot = -1; ot <= 1; ot++) {
            for (let ow = -1; ow <= 1; ow++) {
              const st = t + ot * dt, sw = Math.max(0, wFrac + ow * dw)
              dSum += deckPatchFn(st, isLongboard, sw)
              tSum += tailPatchFn(st, isLongboard, sw)
            }
          }
          dSum /= 9; tSum /= 9
          // Deck patch on deck-facing, tail patch on bottom-facing, both on rails
          const patchSum = isDeckFace ? dSum : (Math.abs(nThick) >= 0.15 ? tSum : Math.max(dSum, tSum))
          if (patchSum > 0.01) {
            c.multiplyScalar(1 - patchSum * 0.15)
          }
        }

        // Pin line — thin stripe on deck face near the rail
        if (pinLineColor && isDeckFace) {
          const wFrac = maxW > 0 ? w / maxW : 0
          if (wFrac > 0.75 && wFrac < 0.80) {
            c.copy(new THREE.Color(pinLineColor))
          }
        }

        // Stringer lines as vertex color
        if (stringerCount >= 1 && stringerCount !== 0) {
          const wSigned = worldPositions[i*3 + widthIdx]
          if (isPerf) {
            const halfStripe = maxW * 0.008
            if (stringerCount === 1 && Math.abs(wSigned) < halfStripe) {
              c.copy(new THREE.Color(stringerColor))
            } else if (stringerCount >= 2) {
              const gap = maxW * 0.06
              const dblStripe = maxW * 0.025
              if (Math.abs(wSigned - gap) < dblStripe || Math.abs(wSigned + gap) < dblStripe) {
                c.copy(new THREE.Color(stringerColor))
              }
            }
          } else {
            const halfStripe = maxW * 0.008
            if (stringerCount === 1 && Math.abs(wSigned) < halfStripe) {
              c.copy(new THREE.Color(stringerColor))
            } else if (stringerCount >= 2) {
              const gap = maxW * 0.06
              const dblStripe = maxW * 0.016
              if (Math.abs(wSigned - gap) < dblStripe || Math.abs(wSigned + gap) < dblStripe) {
                c.copy(new THREE.Color(stringerColor))
              }
            }
          }
        }

        colors[i*3] = c.r
        colors[i*3+1] = c.g
        colors[i*3+2] = c.b

      }

      const colorAttr = new THREE.BufferAttribute(colors, 3)
      colorAttr.needsUpdate = true
      geo.setAttribute("color", colorAttr)
      geo.attributes.color.needsUpdate = true
      if (child.material) child.material.dispose()
      child.material = new THREE.MeshPhysicalMaterial({
        vertexColors: true,
        clearcoat: isGloss ? 1.0 : 0.0,
        clearcoatRoughness: isGloss ? 0.05 : 0,
        roughness: isGloss ? 0.12 : 0.4,
        metalness: 0.0,
        reflectivity: isGloss ? 0.5 : 0.2,
        side: THREE.DoubleSide,
      })
    })

    // Add stringer dups after traverse to avoid corrupting the traversal
    for (const dup of pendingDups) clone.add(dup)

    const logoPos = [0, 0, 0]
    logoPos[lengthIdx] = -boardLength * 0.15
    const logoSize = boardWidth * 0.4

    const isHybrid = modelUrl.includes('Hybrid')
    return { clone, thicknessHalf: boardThick / 2, logoPos, logoSize, thickIdx, lengthIdx, widthIdx, boardLength, boardWidth, boardThick, deckSign, isHybrid }
  }, [scene, deckColor, bottomColor, stringerColor, isGloss, finColor, pinLineColor, deckPatch, tailPatch, modelUrl, finSetup, stringerCount])

  useFrame(({ camera }, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += delta * 0.15
      if (onRotation) {
        const azimuth = Math.atan2(camera.position.x, camera.position.z)
        const effectiveY = meshRef.current.rotation.y + azimuth
        const polar = Math.atan2(Math.sqrt(camera.position.x ** 2 + camera.position.z ** 2), camera.position.y)
        onRotation(effectiveY, polar)
      }
    }
  })

  // Dimension scaling: compute scale factors relative to model's natural size
  const dimScale = useMemo(() => {
    if (!data || !lengthInches || !widthInches) return [0.01, 0.01, 0.01]
    // Model dimensions in arbitrary units — convert to inches for ratio
    // Reference: model units / expected inches gives us the conversion
    const refLengthIn = data.boardLength // model's natural length units
    const refWidthIn = data.boardWidth
    const refThickIn = data.boardThick
    // Scale each axis: (target / reference) * base scale
    const base = 0.01
    const ls = lengthInches ? (lengthInches / 110) : 1 // normalize to ~9'2" reference
    const ws = widthInches ? (widthInches / 22) : 1
    const ts = thicknessInches ? (thicknessInches / 2.75) : 1
    const s = [base, base, base]
    s[data.thickIdx] = base * ts
    s[data.lengthIdx] = base * ls
    s[data.widthIdx] = base * ws
    return s
  }, [data, lengthInches, widthInches, thicknessInches])

  return (
    <group ref={meshRef} rotation={[0, Math.PI / 2, 0]}>
      <group scale={dimScale}>
        <primitive object={data.clone} />
        <LogoPlane logoTex={logoTex} data={data} side={1} />
        <LogoPlane logoTex={logoTex} data={data} side={-1} />
        <LeashPlug data={data} finColor={finColor} />
        <FinBoxes data={data} finColor={finColor} finSetup={finSetup} />
      </group>
    </group>
  )
}

// ── Logo Plane ──────────────────────────────────────────────────────────

function LogoPlane({ logoTex, data, side }) {
  const isPerf = data.deckSign === 1
  const pos = [0, 0, 0]
  pos[data.lengthIdx] = data.logoPos[data.lengthIdx]
  if (isPerf) {
    // Performance models: use thickIdx + deckSign for correct side placement
    const surfaceMult = side > 0 ? -0.12 : 0.94
    pos[data.thickIdx] = side * data.deckSign * data.thicknessHalf * surfaceMult
  } else if (data.isHybrid) {
    pos[data.thickIdx] = side > 0 ? data.thicknessHalf * 0.9 : data.thicknessHalf * 0.09
  } else {
    // Round nose: original positioning
    if (side > 0) {
      pos[0] = data.thicknessHalf * 0.94
    } else {
      pos[0] = -data.thicknessHalf * 0.15
    }
  }

  const euler = new THREE.Euler()
  if (data.thickIdx === 0) {
    euler.set(0, side > 0 ? Math.PI / 2 : -Math.PI / 2, 0)
  } else if (data.thickIdx === 1) {
    euler.set(side > 0 ? -Math.PI / 2 : Math.PI / 2, 0, 0)
  } else {
    euler.set(side < 0 ? Math.PI : 0, 0, 0)
  }
  // Flip bottom logo 180° on performance models so it reads right-side-up
  if (isPerf && side < 0) {
    euler.z += Math.PI
  }

  return (
    <mesh position={pos} rotation={euler}>
      <planeGeometry args={[data.logoSize, data.logoSize]} />
      <meshBasicMaterial map={logoTex} transparent alphaTest={0.1} depthWrite={false} side={THREE.DoubleSide} />
    </mesh>
  )
}

// ── Leash Plug ─────────────────────────────────────────────────────────

function LeashPlug({ data, finColor }) {
  const isPerf = data.deckSign === 1
  const pos = [0, 0, 0]
  pos[data.thickIdx] = isPerf ? data.deckSign * data.thicknessHalf * 0.1 : data.isHybrid ? -data.thicknessHalf * 0.05 : -data.thicknessHalf * 0.6
  pos[data.lengthIdx] = -data.boardLength * 0.44

  const euler = new THREE.Euler()
  if (data.thickIdx === 0) euler.set(0, isPerf ? Math.PI / 2 : -Math.PI / 2, 0)
  else if (isPerf && data.thickIdx === 1) euler.set(-Math.PI / 2, 0, 0)
  else if (isPerf && data.thickIdx === 2) euler.set(0, 0, 0)

  const radius = data.boardWidth * 0.02

  return (
    <mesh position={pos} rotation={euler}>
      <circleGeometry args={[radius, 24]} />
      <meshStandardMaterial color={finColor || 0x333333} roughness={0.8} side={THREE.FrontSide} />
    </mesh>
  )
}

// ── Fin Boxes ──────────────────────────────────────────────────────────

// Positions normalized from SVG: t along length (0=nose, 1=tail), w as fraction of half-width
// SVG board: y 18(nose)-937(tail)=919 range, center x=165, half-width≈120
const FIN_BOX_DEFS = {
  centerLong:  { t: 0.88, len: 0.12, w: 0 },       // center, long box
  centerShort: { t: 0.95, len: 0.05, w: 0, close: true },  // center, short box - sits closer
  left:        { t: 0.84, len: 0.05, w: -0.58 },   // left side
  right:       { t: 0.84, len: 0.05, w: 0.58 },    // right side
  rearLeft:    { t: 0.91, len: 0.05, w: -0.35 },   // rear left
  rearRight:   { t: 0.91, len: 0.05, w: 0.35 },    // rear right
}

// Center box is baked into GLB for Single/2+1/4+1 — render short center + sides for others
const FIN_SETUP_MAP = {
  "Single":    [],
  "2+1":       ["left", "right"],
  "Twin":      ["left", "right"],
  "Thruster":  ["centerShort", "left", "right"],
  "Quad":      ["left", "right", "rearLeft", "rearRight"],
  "4+1":       ["left", "right", "rearLeft", "rearRight"],
  "5-Fin":     ["centerShort", "left", "right", "rearLeft", "rearRight"],
}

function FinBox({ data, finColor, def }) {
  const boxWidth = data.boardWidth * 0.03
  const boxLen = data.boardLength * def.len
  const boxThick = data.boardThick * 0.08

  const pos = [0, 0, 0]
  const isPerf = data.deckSign === 1
  const usesPerfPos = isPerf || data.isHybrid
  const rawT = def.close ? -0.3 : def.t > 0.88 ? -0.15 : 0.20
  const perfMult = def.close ? 1.0 : def.t > 0.88 ? 2.2 : 2.7
  if (data.isHybrid) {
    const hMult = def.close ? 0.7 : def.t >= 0.91 ? 2.1 : 2.5
    pos[data.thickIdx] = data.thicknessHalf * Math.abs(rawT) * hMult
  } else {
    pos[data.thickIdx] = isPerf ? -data.deckSign * data.thicknessHalf * Math.abs(rawT) * perfMult : data.thicknessHalf * rawT
  }
  pos[data.lengthIdx] = -data.boardLength * (def.t - 0.5)
  pos[data.widthIdx] = data.boardWidth * 0.5 * def.w

  const size = [0, 0, 0]
  size[data.thickIdx] = boxThick
  size[data.lengthIdx] = boxLen
  size[data.widthIdx] = boxWidth

  // Tilt to match board curvature
  const rot = [0, 0, 0]
  // Rocker tilt along length axis (tail kicks up)
  rot[data.widthIdx] = def.close ? -0.18 : (def.t > 0.88 ? -0.12 : -0.08)
  // Cross-section tilt for side fins
  if (def.w !== 0) {
    rot[data.lengthIdx] = def.w > 0 ? -0.15 : 0.15
  }

  return (
    <mesh position={pos} rotation={rot}>
      <boxGeometry args={size} />
      <meshPhysicalMaterial color={finColor || 0x333333} roughness={0.3} metalness={0} side={THREE.DoubleSide} />
    </mesh>
  )
}

function FinBoxes({ data, finColor, finSetup }) {
  const boxes = FIN_SETUP_MAP[finSetup] || FIN_SETUP_MAP["Thruster"]
  return (
    <group>
      {boxes.map((key) => (
        <FinBox key={key} data={data} finColor={finColor} def={FIN_BOX_DEFS[key]} />
      ))}
    </group>
  )
}

// ── Board Glow ──────────────────────────────────────────────────────────

function AnimatedLight({ position, color, baseIntensity, distance, speed }) {
  const ref = useRef()
  const offset = useRef(Math.random() * Math.PI * 2)
  useFrame(({ clock }) => {
    if (ref.current) {
      const t = clock.elapsedTime * speed + offset.current
      ref.current.intensity = baseIntensity * (0.6 + Math.sin(t) * 0.4)
    }
  })
  return <pointLight ref={ref} position={position} color={color} intensity={baseIntensity} distance={distance} />
}

function BoardGlow({ deckColor, bottomColor }) {
  const glowColor = useMemo(() => new THREE.Color(deckColor).lerp(new THREE.Color(bottomColor), 0.5), [deckColor, bottomColor])

  return (
    <>
      <AnimatedLight position={[0, 0, -2]} color={glowColor} baseIntensity={0.5} distance={8} speed={0.3} />
      <AnimatedLight position={[0, 0.5, -1.5]} color={glowColor} baseIntensity={0.3} distance={6} speed={0.5} />
      <AnimatedLight position={[0, -0.5, -1.5]} color={glowColor} baseIntensity={0.3} distance={6} speed={0.7} />
      <AnimatedLight position={[1, 0, -1.2]} color={glowColor} baseIntensity={0.2} distance={5} speed={0.4} />
      <AnimatedLight position={[-1, 0, -1.2]} color={glowColor} baseIntensity={0.2} distance={5} speed={0.6} />
    </>
  )
}

// ── UI Components ───────────────────────────────────────────────────────

const BOARD_COLORS = [
  { name: "Clear", hex: "#F5F5F5" },
  { name: "Red", hex: "#ff0000" },
  { name: "Strawberry", hex: "#ff6e6e" },
  { name: "Light Pink", hex: "#fac0c0" },
  { name: "Hot Pink", hex: "#ff035f" },
  { name: "Pale Peach", hex: "#fae6c0" },
  { name: "Orange", hex: "#ff8800" },
  { name: "Light Orange", hex: "#ffd478" },
  { name: "Caramel", hex: "#d99800" },
  { name: "Copper", hex: "#a34100" },
  { name: "Yellow", hex: "#fff200" },
  { name: "Butter", hex: "#fff491" },
  { name: "Sage", hex: "#a2cf76" },
  { name: "Lime Green", hex: "#73ff00" },
  { name: "Avocado", hex: "#5c8535" },
  { name: "Seafoam", hex: "#00ffae" },
  { name: "Cyan", hex: "#00ffff" },
  { name: "Sky Blue", hex: "#00b7ff" },
  { name: "Blue", hex: "#005eff" },
  { name: "Lavender", hex: "#f4baff" },
  { name: "Purple", hex: "#cc00ff" },
  { name: "Navy", hex: "#1a3a5c" },
  { name: "Teal", hex: "#2ca5a5" },
  { name: "Coral", hex: "#ff7f7f" },
  { name: "Mint", hex: "#b8e8d0" },
  { name: "Charcoal", hex: "#555555" },
]

const STRINGER_COLORS = [
  { name: "Basswood", hex: "#fff9c7" },
  { name: "Darkwood", hex: "#3a2e2e" },
  { name: "Redwood", hex: "#a64602" },
]

const COLOR_MODES = [
  { label: "Deck Only", deck: true, bottom: false },
  { label: "Bottom Only", deck: false, bottom: true },
  { label: "Both Sides", deck: true, bottom: true },
]

function sineWavePath(W, H, amp, phase, cycles, centerY) {
  const cy = centerY != null ? centerY : H / 2;
  const pts = [];
  for (let x = 0; x <= W; x += 3) {
    const t = (x / W) * cycles * 2 * Math.PI;
    const ampVar = 1 + 0.35 * Math.sin(t * 0.37 + phase * 1.7) + 0.2 * Math.sin(t * 0.71 + phase * 0.9) + 0.1 * Math.sin(t * 1.53 + phase * 2.3);
    const y = cy + amp * ampVar * Math.sin(phase + t);
    pts.push((x === 0 ? "M" : "L") + x + "," + y.toFixed(1));
  }
  return pts.join(" ");
}

const LOGO_OPTIONS = [
  { label: "Diamond", value: "diamond" },
  { label: "Fish", value: "fish" },
]

const DECK_PATCHES = ["None", "Straight Point", "Curved Point", "Round"]
const TAIL_PATCHES = ["None", "Straight Point", "Reverse Straight", "Curved Point", "Reverse Curved", "Round"]

const LABEL_SX = { color: "#8a9bb0", fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: "0.25em", fontFamily: "'Josefin Sans', sans-serif", mb: 0.8, textAlign: "center" }

function SwatchRow({ label, colors, selected, onSelect, square }) {
  return (
    <Box sx={{ mb: 1.2 }}>
      {label && <Typography sx={LABEL_SX}>{label}</Typography>}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5, flexWrap: "wrap" }}>
        {colors.map((c) => {
          const active = selected === c.hex
          const isLight = ["#ffffff", "#fff200", "#F5F5F5", "#fff9c7", "#fae6c0", "#ffd478", "#fff491", "#b8e8d0", "#fac0c0", "#f4baff", "#00ffff", "#00ffae", "#73ff00"].includes(c.hex)
          return (
            <Box
              key={c.hex} onClick={() => onSelect(c.hex)} title={c.name}
              sx={square ? {
                width: 32, height: 32, borderRadius: 1, bgcolor: c.hex, cursor: "pointer",
                border: active ? "2px solid #c9a84c" : isLight ? "1px solid rgba(255,255,255,0.15)" : "1px solid transparent",
                boxShadow: active ? "0 0 6px rgba(200,185,154,0.4)" : "none",
                transition: "all 0.15s", "&:hover": { border: "2px solid rgba(255,255,255,0.4)", transform: "scale(1.08)" },
              } : {
                width: 22, height: 22, borderRadius: "50%", bgcolor: c.hex, cursor: "pointer",
                border: active ? "2px solid #c9a84c" : "2px solid #1e2d3d",
                transition: "border 0.2s ease", "&:hover": { border: "2px solid #c9a84c" },
              }}
            />
          )
        })}
      </Box>
    </Box>
  )
}

function OptionRow({ label, options, selected, onSelect }) {
  return (
    <Box sx={{ mb: 1.2 }}>
      <Typography sx={LABEL_SX}>{label}</Typography>
      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.3, flexWrap: "wrap" }}>
        {options.map((opt) => {
          const val = typeof opt === 'string' ? opt : opt.value
          const lbl = typeof opt === 'string' ? opt : opt.label
          const active = selected === val
          return (
            <Box key={val} onClick={() => onSelect(val)}
              sx={{ px: 1, py: 0.4, cursor: "pointer", fontSize: "0.5rem", textTransform: "uppercase", letterSpacing: "0.15em", fontFamily: "'Josefin Sans', sans-serif",
                color: active ? "#f0ede8" : "#8a9bb0",
                bgcolor: active ? "rgba(201,168,76,0.15)" : "transparent",
                border: active ? "1px solid rgba(201,168,76,0.4)" : "1px solid transparent",
                borderRadius: 0,
                transition: "all 0.2s ease",
                "&:hover": { color: "#f0ede8" },
              }}
            >{lbl}</Box>
          )
        })}
      </Box>
    </Box>
  )
}

// ── Main Viewer ─────────────────────────────────────────────────────────

class ThreeErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null } }
  static getDerivedStateFromError(error) { return { error } }
  render() {
    if (this.state.error) return (
      <Box sx={{ color: '#fff', p: 4, fontFamily: "'Josefin Sans', sans-serif" }}>
        <Typography variant="h6">3D Viewer Error</Typography>
        <Typography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>{this.state.error.message}</Typography>
      </Box>
    )
    return this.props.children
  }
}

const NOSE_SHAPES = [
  { label: "Round", value: "Round" },
  { label: "Performance", value: "Performance" },
  { label: "Hybrid", value: "Hybrid" },
]

const TAIL_SHAPES = [
  { label: "Squash", value: "Squash" },
  { label: "Square", value: "Square" },
  { label: "Round", value: "Round" },
  { label: "Diamond", value: "Diamond" },
  { label: "Pin", value: "Pin" },
]

export default function BoardViewer3D() {
  const params = useMemo(() => new URLSearchParams(window.location.search), [])
  const [boardColor, setBoardColor] = useState(() => {
    const dc = params.get("deckColor"), bc = params.get("bottomColor")
    const isClear = (c) => !c || c === "#F5F5F5" || c === "#ffffff"
    if (dc && !isClear(dc)) return dc
    if (bc && !isClear(bc)) return bc
    return "#ff0000"
  })
  const [colorMode, setColorMode] = useState(() => {
    const dc = params.get("deckColor"), bc = params.get("bottomColor")
    if (!dc && !bc) return 2
    const isClear = (c) => !c || c === "#F5F5F5" || c === "#ffffff"
    if (isClear(dc) && !isClear(bc)) return 1   // bottom only
    if (!isClear(dc) && isClear(bc)) return 0    // deck only
    return 2                                      // both sides
  })
  const [stringerColor, setStringerColor] = useState(params.get("stringerColor") || "#fff9c7")
  const [isGloss, setIsGloss] = useState(params.get("isGloss") !== "0")
  const [logoType, setLogoType] = useState(params.get("logoType") || "diamond")
  const [deckPatch, setDeckPatch] = useState(params.get("deckPatch") || "None")
  const [tailPatch, setTailPatch] = useState(params.get("tailPatch") || "None")
  const [finColor, setFinColor] = useState(params.get("finColor") || "#000000")
  const [finSetup, setFinSetup] = useState(params.get("finSetup") || "Thruster")
  const [stringerCount, setStringerCount] = useState(() => {
    const v = params.get("stringerCount")
    return v !== null ? Number(v) : 1
  })
  const [noseShape, setNoseShape] = useState(params.get("noseShape") || "Round")
  const [tailShape, setTailShape] = useState(params.get("tailShape") || "Square")
  const [pinLineColor] = useState(params.get("pinLineColor") || null)

  const CLEAR = "#F5F5F5"
  const mode = COLOR_MODES[colorMode]
  const deckColor = mode.deck ? boardColor : CLEAR
  const bottomColor = mode.bottom ? boardColor : CLEAR
  const modelUrl = getModelUrl(noseShape, tailShape)

  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [wavePhase, setWavePhase] = useState(0)
  useEffect(() => {
    let raf;
    const tick = () => { setWavePhase(p => p + 0.025); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [])

  const SIDEBAR_W = 300
  const SECTION_SX = { color: "#c9a84c", fontFamily: "'Kenilla', 'Playfair Display', serif", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.15em", mt: 3, mb: 1.5, textAlign: "center" }
  const DIVIDER_SX = { borderTop: "1px solid #1e2d3d", my: 1.5 }

  const backToOrder = () => {
    const norm = (c) => c === "#F5F5F5" ? "#ffffff" : c
    const p = new URLSearchParams({
      deckColor: norm(deckColor), bottomColor: norm(bottomColor), stringerColor, finColor, noseShape, tailShape,
      isGloss: isGloss ? "1" : "0", logoType, deckPatch, tailPatch,
      finSetup, stringerCount: String(stringerCount),
      ...(pinLineColor ? { pinLineColor } : {}),
    })
    window.location.href = `/custom-surfboard-order?${p.toString()}`
  }

  const controls = (
    <>
      {/* Shape */}
      <Typography sx={SECTION_SX}>Shape</Typography>
      <OptionRow label="Nose" options={NOSE_SHAPES} selected={noseShape} onSelect={setNoseShape} />
      <OptionRow label="Tail" options={TAIL_SHAPES} selected={tailShape} onSelect={setTailShape} />
      <Box sx={DIVIDER_SX} />

      {/* Color */}
      <Typography sx={SECTION_SX}>Color</Typography>
      <SwatchRow label="" colors={BOARD_COLORS} selected={boardColor} onSelect={setBoardColor} square />
      <Box sx={{ display: "flex", gap: 0.4, justifyContent: "center", mt: 0.3 }}>
        {COLOR_MODES.map((m, i) => (
          <Box key={m.label} onClick={() => setColorMode(i)}
            sx={{ px: 1, py: 0.3, cursor: "pointer", fontSize: "0.5rem", textTransform: "uppercase", letterSpacing: "0.2em", fontFamily: "'Josefin Sans', sans-serif",
              color: colorMode === i ? "#f0ede8" : "#8a9bb0",
              bgcolor: colorMode === i ? "rgba(201,168,76,0.15)" : "transparent",
              border: colorMode === i ? "1px solid rgba(201,168,76,0.4)" : "1px solid transparent",
              transition: "all 0.2s ease", "&:hover": { color: "#f0ede8" },
            }}
          >{m.label}</Box>
        ))}
      </Box>
      <Box sx={DIVIDER_SX} />

      {/* Details */}
      <Typography sx={SECTION_SX}>Details</Typography>
      <SwatchRow label="Stringer Color" colors={STRINGER_COLORS} selected={stringerColor} onSelect={setStringerColor} square />
      <OptionRow label="Stringers" options={[{label: "None", value: 0}, {label: "Single", value: 1}, {label: "Double", value: 2}]} selected={stringerCount} onSelect={setStringerCount} />
      {/* Logo tiles */}
      <Box sx={{ mb: 1.2 }}>
        <Typography sx={LABEL_SX}>Logo</Typography>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          {[{ value: "diamond", src: "/static/models/marlin-logo.png" }, { value: "fish", src: "/static/models/marlin-fish-logo.png" }].map((l) => (
            <Box key={l.value} onClick={() => setLogoType(l.value)}
              sx={{
                width: 48, height: 48, borderRadius: 1, cursor: "pointer",
                border: logoType === l.value ? "2px solid #c9a84c" : "1px solid #1e2d3d",
                boxShadow: logoType === l.value ? "0 0 6px rgba(200,185,154,0.4)" : "none",
                bgcolor: "#0d1b2a", display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.15s", "&:hover": { border: "2px solid rgba(255,255,255,0.4)", transform: "scale(1.08)" },
              }}
            >
              <img src={l.src} alt={l.value} style={{ width: 36, height: 36, objectFit: "contain" }} />
            </Box>
          ))}
        </Box>
      </Box>
      <OptionRow label="Finish" options={[{label: "Gloss", value: "gloss"}, {label: "Matte", value: "matte"}]} selected={isGloss ? "gloss" : "matte"} onSelect={(v) => setIsGloss(v === "gloss")} />
      <Box sx={DIVIDER_SX} />

      {/* Patches */}
      <Typography sx={SECTION_SX}>Patches</Typography>
      <OptionRow label="Deck" options={DECK_PATCHES} selected={deckPatch} onSelect={setDeckPatch} />
      <OptionRow label="Tail Pad" options={TAIL_PATCHES} selected={tailPatch} onSelect={setTailPatch} />
      <Box sx={DIVIDER_SX} />

      {/* Fins */}
      <Typography sx={SECTION_SX}>Fins</Typography>
      <SwatchRow label="Color" colors={[{name: "Black", hex: "#000000"}, {name: "White", hex: "#ffffff"}]} selected={finColor} onSelect={setFinColor} square />
      <OptionRow label="Setup" options={[{label: "Single", value: "Single"}, {label: "Twin", value: "Twin"}, {label: "Thruster", value: "Thruster"}, {label: "Quad", value: "Quad"}, {label: "2+1", value: "2+1"}, {label: "4+1", value: "4+1"}, {label: "5-Fin", value: "5-Fin"}]} selected={finSetup} onSelect={setFinSetup} />
    </>
  )

  return (
    <Box sx={{ width: "100vw", height: "100vh", bgcolor: "#0d1b2a", position: "relative", overflow: "hidden" }}>
      {/* Background sine waves */}
      <svg style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 0 }} viewBox="0 0 600 400" preserveAspectRatio="none">
        <defs>
          <linearGradient id="bvWave1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#c8b99a" stopOpacity="0.06"/><stop offset="100%" stopColor="#c8b99a" stopOpacity="0"/></linearGradient>
          <linearGradient id="bvWave2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#c8b99a" stopOpacity="0.04"/><stop offset="100%" stopColor="#c8b99a" stopOpacity="0"/></linearGradient>
        </defs>
        <path d={sineWavePath(600, 400, 20, -wavePhase * 0.5, 1.5, 320) + " L600,400 L0,400 Z"} fill="url(#bvWave1)"/>
        <path d={sineWavePath(600, 400, 15, -wavePhase * 0.3 + 1, 2, 350) + " L600,400 L0,400 Z"} fill="url(#bvWave2)"/>
        <path d={sineWavePath(600, 400, 20, -wavePhase * 0.5, 1.5, 320)} fill="none" stroke="#c8b99a" strokeWidth="0.8" opacity="0.15"/>
        <path d={sineWavePath(600, 400, 15, -wavePhase * 0.3 + 1, 2, 350)} fill="none" stroke="#c8b99a" strokeWidth="0.6" opacity="0.1"/>
      </svg>

      {/* ── Canvas (always full screen) ── */}
      <Box sx={{ position: "absolute", top: 0, left: isDesktop ? SIDEBAR_W : 0, right: 0, bottom: 0 }}>
        {/* 2D CSS glow behind board */}
        <Box sx={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: "60%", height: "70%", borderRadius: "50%",
          background: `radial-gradient(ellipse, ${(deckColor === "#F5F5F5" || deckColor === "#ffffff") ? ((bottomColor === "#F5F5F5" || bottomColor === "#ffffff") ? "rgba(200,185,154,0.15)" : bottomColor + "44") : deckColor + "44"} 0%, transparent 70%)`,
          filter: "blur(60px)", pointerEvents: "none", zIndex: 0,
        }} />
        <ThreeErrorBoundary>
        <Canvas
          camera={{ position: [0, 0.5, 5.5], fov: 35 }}
          gl={{ antialias: true, toneMapping: THREE.LinearToneMapping }}
          dpr={[1, 2]}
        >
          <ambientLight intensity={0.6} />
          <directionalLight position={[5, 5, 5]} intensity={0.5} />
          <directionalLight position={[-5, -5, -5]} intensity={0.5} />
          <Suspense fallback={null}>
            <SurfboardModel
              modelUrl={modelUrl}
              deckColor={deckColor} bottomColor={bottomColor}
              stringerColor={stringerColor} isGloss={isGloss}
              logoType={logoType} deckPatch={deckPatch} tailPatch={tailPatch} finColor={finColor} finSetup={finSetup} stringerCount={stringerCount}
            />
            <BoardGlow deckColor={deckColor} bottomColor={bottomColor} />
            <Environment preset="studio" environmentIntensity={0.4} />
          </Suspense>
          <OrbitControls enablePan enableZoom minDistance={0.3} maxDistance={20} minPolarAngle={0} maxPolarAngle={Math.PI} />
        </Canvas>
        </ThreeErrorBoundary>
      </Box>

      {/* ── Desktop Sidebar ── */}
      {isDesktop && (
        <Box sx={{
          position: "absolute", top: 0, left: 0, bottom: 0,
          width: SIDEBAR_W,
          bgcolor: "rgba(13,27,42,0.95)", backdropFilter: "blur(16px)",
          borderRight: "1px solid #1e2d3d",
          display: "flex", flexDirection: "column",
          zIndex: 2,
        }}>
          {/* Header */}
          <Box sx={{ px: 2.5, pt: 3, pb: 1.5 }}>
            <Typography sx={{ color: "#c8b99a", fontFamily: "'Kenilla', 'Playfair Display', serif", fontSize: "1.4rem", letterSpacing: "0.1em", textTransform: "uppercase", lineHeight: 1.2 }}>
              Marlin Surfboards
            </Typography>
            <Typography sx={{ color: "#8a9bb0", fontFamily: "'Josefin Sans', sans-serif", fontSize: "0.55rem", textTransform: "uppercase", letterSpacing: "0.25em", mt: 0.3 }}>
              Board Viewer
            </Typography>
            <Box onClick={backToOrder} sx={{ mt: 1.5, cursor: "pointer" }}>
              <Typography sx={{ color: "#8a9bb0", fontFamily: "'Josefin Sans', sans-serif", fontSize: "0.55rem", textTransform: "uppercase", letterSpacing: "0.2em", transition: "color 0.2s ease", "&:hover": { color: "#c9a84c" } }}>
                ← Back to Custom Order
              </Typography>
            </Box>
          </Box>
          <Box sx={{ borderTop: "1px solid #1e2d3d" }} />

          {/* Controls — scrollable */}
          <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, pb: 3,
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(201,168,76,0.3)", borderRadius: 2 },
          }}>
            {controls}
          </Box>
        </Box>
      )}

      {/* ── Mobile Overlay ── */}
      {!isDesktop && (
        <>
          {/* Top bar */}
          <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, px: 2, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 2 }}>
            <Box>
              <Typography sx={{ color: "#c8b99a", fontFamily: "'Kenilla', 'Playfair Display', serif", fontSize: "1.1rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Marlin Surfboards
              </Typography>
              <Typography sx={{ color: "#8a9bb0", fontFamily: "'Josefin Sans', sans-serif", fontSize: "0.45rem", textTransform: "uppercase", letterSpacing: "0.25em" }}>
                Board Viewer
              </Typography>
            </Box>
            <Box onClick={backToOrder} sx={{ cursor: "pointer" }}>
              <Typography sx={{ color: "#8a9bb0", fontFamily: "'Josefin Sans', sans-serif", fontSize: "0.5rem", textTransform: "uppercase", letterSpacing: "0.15em", "&:hover": { color: "#c9a84c" } }}>
                ← Custom Order
              </Typography>
            </Box>
          </Box>

          {/* Drawer toggle */}
          <Box
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{
              position: "absolute", bottom: drawerOpen ? "55vh" : 0, left: "50%", transform: "translateX(-50%)",
              zIndex: 3, cursor: "pointer", transition: "bottom 0.3s ease",
              bgcolor: "rgba(13,27,42,0.9)", borderTop: "1px solid #1e2d3d", borderLeft: "1px solid #1e2d3d", borderRight: "1px solid #1e2d3d",
              borderRadius: "8px 8px 0 0", px: 3, py: 0.5,
            }}
          >
            <Typography sx={{ color: "#c9a84c", fontFamily: "'Josefin Sans', sans-serif", fontSize: "0.5rem", textTransform: "uppercase", letterSpacing: "0.25em" }}>
              {drawerOpen ? "▼ Close" : "▲ Options"}
            </Typography>
          </Box>

          {/* Drawer content */}
          <Box sx={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            height: drawerOpen ? "55vh" : 0, overflow: "hidden",
            transition: "height 0.3s ease", zIndex: 2,
            bgcolor: "rgba(13,27,42,0.95)", backdropFilter: "blur(16px)",
            borderTop: drawerOpen ? "1px solid #1e2d3d" : "none",
          }}>
            <Box sx={{ height: "100%", overflowY: "auto", px: 2.5, pb: 3,
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(201,168,76,0.3)", borderRadius: 2 },
            }}>
              {controls}
            </Box>
          </Box>
        </>
      )}
    </Box>
  )
}

// ── Embeddable 3D Viewer (no bg, no controls UI) ───────────────────────

export function BoardViewer3DEmbed({ deckColor = "#F5F5F5", bottomColor = "#F5F5F5", stringerColor = "#fff9c7", isGloss = true, logoType = "diamond", finColor = "#000000", onRotation, noseShape = "Round", tailShape = "Square", lengthInches, widthInches, thicknessInches, pinLineColor, deckPatch = "None", tailPatch = "None", finSetup = "Thruster", stringerCount = 1 }) {
  const modelUrl = getModelUrl(noseShape, tailShape)
  return (
    <ThreeErrorBoundary>
    <Canvas
      camera={{ position: [0, 0.3, 6.5], fov: 35, near: 0.1 }}
      gl={{ antialias: true, toneMapping: THREE.LinearToneMapping, alpha: true }}
      dpr={[1, 2]}
      style={{ background: "transparent", width: "100%", height: "100%" }}
    >
      <ambientLight intensity={0.6} />
      <directionalLight position={[5, 5, 5]} intensity={0.5} />
      <directionalLight position={[-5, -5, -5]} intensity={0.5} />

      <Suspense fallback={null}>
        <SurfboardModel
          modelUrl={modelUrl}
          deckColor={deckColor} bottomColor={bottomColor}
          stringerColor={stringerColor} isGloss={isGloss}
          logoType={logoType} deckPatch={deckPatch} tailPatch={tailPatch} finColor={finColor} finSetup={finSetup} stringerCount={stringerCount}
          onRotation={onRotation}
          lengthInches={lengthInches} widthInches={widthInches} thicknessInches={thicknessInches}
          pinLineColor={pinLineColor}
        />
        <Environment preset="studio" environmentIntensity={0.2} />
      </Suspense>

      <OrbitControls enablePan={false} enableZoom={false} minPolarAngle={0} maxPolarAngle={Math.PI} />
    </Canvas>
    </ThreeErrorBoundary>
  )
}
